import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { IMAGES } from "../lib/site";
import { PageHero, SectionHeading } from "../components/Sections";

const galleryItems = [
  { tag: "BBL", caption: "Brazilian Butt Lift · 6 months post-op" },
  { tag: "Breast Augmentation", caption: "Silicone, 350cc · 3 months post-op" },
  { tag: "Body Contouring", caption: "Lipo 360 + BBL · 4 months post-op" },
  { tag: "Mommy Makeover", caption: "Abdominoplasty + Breast Lift · 1 year" },
  { tag: "Breast Lift", caption: "Mastopexy with implants · 6 months" },
  { tag: "Revision", caption: "Implant exchange & repositioning · 3 months" },
];

export default function GalleryPage() {
  return (
    <div data-testid="page-gallery">
      <PageHero
        kicker="Before & After"
        title="Results, told with"
        accent="discretion"
        subtitle="A curated selection of patient transformations. All imagery is shared with written consent. Names withheld for privacy."
        image={IMAGES.gallerySilhouette}
        testid="gallery-hero"
      />

      <section className="py-12 bg-mag border-b border-[rgba(212,175,55,0.18)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center gap-4">
          <Lock size={20} className="text-gold shrink-0" />
          <p className="text-white/65 text-sm font-body leading-relaxed" data-testid="gallery-disclaimer">
            Individual results vary. All before-and-after representations are
            illustrative and used with explicit, written patient consent.
            High-resolution case studies are reviewed privately during your
            in-person consultation in compliance with HIPAA.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-mag">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            kicker="Selected Studies"
            title="A portfolio of"
            accent="quiet beauty"
            align="center"
            testid="gallery-grid-heading"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-16">
            {galleryItems.map((g, i) => (
              <div
                key={i}
                className="group relative overflow-hidden border border-[rgba(212,175,55,0.25)] bg-mag-2"
                data-testid={`gallery-card-${i}`}
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={IMAGES.gallerySilhouette}
                    alt=""
                    className="w-full h-full object-cover opacity-55 group-hover:opacity-75 transition duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-5 left-5 px-3 py-1 border border-[rgba(212,175,55,0.5)] bg-black/60">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold">{g.tag}</span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center px-6">
                      <p className="font-brand text-pink-luxe text-5xl leading-none">Before / After</p>
                      <p className="text-white/55 text-[10px] tracking-[0.35em] uppercase mt-3">Available in private viewing</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-[rgba(212,175,55,0.18)]">
                  <p className="font-heading text-white text-xl">{g.caption}</p>
                  <p className="text-white/45 text-[11px] tracking-[0.25em] uppercase mt-1">Patient anonymous</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-mag-2">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h3 className="font-heading text-4xl md:text-5xl text-white">
            View full case studies in{" "}
            <span className="font-brand text-pink-luxe text-5xl md:text-6xl">private consultation</span>
          </h3>
          <p className="text-white/65 font-heading text-lg mt-6 max-w-2xl mx-auto">
            Detailed before-and-after archives, tailored to your goals, are
            shared confidentially during your visit.
          </p>
          <Link to="/consultations" className="btn-gold mt-8" data-testid="gallery-cta">
            Reserve Private Viewing <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
